import React from "react"
import {useTranslation} from "react-i18next"

// HOCs
import withRecordData, {WithRecordDataProps} from "../../../hoc/withRecordData"

// Models
import {Record as RecordModel} from "../../../models"

// Utils
import {acceptsParameter, PARAM_LIMITS} from "../paramLimits"

// Styles
import useStyles from "./styles"

const EM_DASH = "—";

type CommonParameters = NonNullable<RecordModel["commonParameters"]>
export type ParameterKey = keyof CommonParameters

type ParamDef = {
    key: ParameterKey
    /** Clinical abbreviation — intentionally not translated. */
    label: string
    fullLabel?: string
    unit: string
    /**
     * Reference range printed under the value exactly as on a cart's report.
     * Numeric ranges are locale-independent and stay literal; prose ones carry
     * an i18n key in `rangeKey` instead.
     */
    range: string
    /** i18n key for a prose range ("RR · regular", "measured"). */
    rangeKey?: string
    editable: boolean
    /** "Interval" / "Axis" / "HR" — used for the accessible full name. */
    kind: "interval" | "axis" | "hr"
    heart?: boolean
}

/**
 * The design prototype's ten measurement cells, in its order, plus `tDuration`.
 *
 * `tDuration` is not in the prototype but IS in production's `commonParameters`
 * and was on the previous version of this card, so dropping it would quietly
 * remove a measurement reviewers can currently see and edit. `qtcfInterval` is
 * the reverse case: it exists on the model, Classic never renders it, and the
 * prototype does — it is shown here, read-only, alongside Bazett.
 *
 * Both QTc cells are non-editable because they are derived values; typing over
 * a correction formula's output would make it disagree with the QT and RR it is
 * computed from.
 */
const PARAM_DEFS: ParamDef[] = [
    {key: "heartRate", label: "HR", unit: "bpm", range: "", rangeKey: "modern.range_rr_regular", editable: true, kind: "hr", heart: true},
    {key: "prInterval", label: "PR", unit: "ms", range: "120–200", editable: true, kind: "interval"},
    {key: "qrsDuration", label: "QRS", unit: "ms", range: "< 120", editable: true, kind: "interval"},
    {key: "qtInterval", label: "QT", unit: "ms", range: "", rangeKey: "modern.range_measured", editable: true, kind: "interval"},
    {key: "pDuration", label: "P / dur", unit: "ms", range: "< 120", editable: true, kind: "interval"},
    {key: "tDuration", label: "T / dur", unit: "ms", range: "", rangeKey: "modern.range_measured", editable: true, kind: "interval"},
    {key: "pAxis", label: "P axis", unit: "°", range: "0–75", editable: true, kind: "axis"},
    {key: "qrsAxis", label: "QRS axis", unit: "°", range: "−30–90", editable: true, kind: "axis"},
    {key: "tAxis", label: "T axis", unit: "°", range: "15–75", editable: true, kind: "axis"},
    {key: "qtcInterval", label: "QTcB", fullLabel: "QTc (Bazett)", unit: "ms", range: "", editable: false, kind: "interval"},
    {key: "qtcfInterval", label: "QTcF", fullLabel: "QTc (Fridericia)", unit: "ms", range: "", editable: false, kind: "interval"},
];

/** Imperative surface the page uses to write a caliper measurement into a cell. */
export type ModernParamsApi = {
    apply(key: ParameterKey, value: number): void
}

type ModernParamsCardOwnProps = {
    /** False on the preview route, for company masters and for approved records. */
    editable: boolean
    apiRef: React.MutableRefObject<ModernParamsApi | null>
    /**
     * Reports "a cell is open for editing, or a save is in flight" up to the
     * page, which gates Approve — the Modern equivalent of the
     * `isParameterEditing` flag Classic used for the same purpose.
     */
    onBusyChange?(busy: boolean): void
}

type ModernParamsCardProps = ModernParamsCardOwnProps & WithRecordDataProps

/**
 * Auto-measured parameters, inline editable.
 *
 * Modern autosaves each cell on commit instead of using Classic's explicit
 * edit / save / cancel mode, so it deliberately never dispatches
 * `toggleParameterEditing` — that flag is shared redux state which gates
 * Classic's Approve button and must stay false here.
 *
 * @param props
 */
function ModernParamsCard(props: ModernParamsCardProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {
        commonParameters,
        parametersUpdating,
        parametersUpdated,
        parametersError,
        updateParameters,
        editable,
        apiRef,
        onBusyChange,
    } = props;

    const [params, setParams] = React.useState<CommonParameters | null>(commonParameters || null);
    const [editingKey, setEditingKey] = React.useState<ParameterKey | null>(null);
    const [flash, setFlash] = React.useState<{key: ParameterKey | null, n: number}>({key: null, n: 0});
    const [rejected, setRejected] = React.useState(false);
    const [localError, setLocalError] = React.useState<string | null>(null);
    // Read by commit(), which is also called from the measure bar's imperative
    // handle and so cannot rely on the render's `params` closure being current.
    const paramsRef = React.useRef<CommonParameters | null>(params);
    const inFlightRef = React.useRef(false);

    paramsRef.current = params;

    // `selectCommonParameters` returns null before the record lands, and the
    // reducer replaces the object after every successful save.
    React.useEffect(() => {
        setParams(commonParameters || null)
    }, [commonParameters]);

    /*
     * Roll back a rejected edit.
     *
     * This card is optimistic: it paints the typed value immediately and only
     * then PATCHes. The server range-checks these fields and answers 400 on an
     * implausible one (a 732 ms PR, say). On that path `updateRecordParameters`
     * fails, the reducer clears `updatingParameters` and leaves
     * `commonParameters` untouched — so the store never changes identity, the
     * sync effect above never re-runs, and without this the rejected number
     * would sit in the cell looking saved for the rest of the review. On a
     * clinical measurement that is the worst possible failure mode.
     *
     * `updatedParameters` is the discriminator the reducer already keeps: true
     * only after a success, false after a request or a failure.
     */
    React.useEffect(() => {
        if (parametersUpdating) {
            inFlightRef.current = true;
            setRejected(false);
            setLocalError(null);

            return
        }

        if (!inFlightRef.current) {
            return
        }

        inFlightRef.current = false;

        if (!parametersUpdated) {
            setParams(commonParameters || null);
            setFlash({key: null, n: 0});
            setRejected(true)
        }
    }, [parametersUpdating, parametersUpdated, commonParameters]);

    const busy = parametersUpdating || editingKey !== null;

    React.useEffect(() => {
        if (!onBusyChange) {
            return () => undefined
        }

        onBusyChange(editable && busy);

        return () => onBusyChange(false)
    }, [editable, busy, onBusyChange]);

    const handleEditingChange = React.useCallback((key: ParameterKey, editing: boolean) => {
        // Only the cell that owns the current edit may clear it: cells commit on
        // blur, and clicking straight from one cell to another opens the second
        // before the first reports its close.
        setEditingKey(current => editing ? key : (current === key ? null : current))
    }, []);

    function fullName(def: ParamDef): string {
        if (def.kind === "hr") {
            return t("records.hr")
        }

        if (def.kind === "axis") {
            return `${t("records.axis")} ${def.label.replace(" axis", "")}`
        }

        return `${t("records.interval")} ${def.fullLabel || def.label}`
    }

    const commit = React.useCallback((key: ParameterKey, value: number) => {
        if (isNaN(value)) {
            return
        }

        if (!acceptsParameter(key, value)) {
            const limit = PARAM_LIMITS[key];
            const def = PARAM_DEFS.find(candidate => candidate.key === key);

            setRejected(false);
            setLocalError(t("modern.out_of_range", {
                label: def ? def.label : key,
                value,
                min: limit.min,
                max: limit.max,
            }));

            return
        }

        setLocalError(null);

        const next: CommonParameters = {...(paramsRef.current || {})};

        next[key] = value;
        paramsRef.current = next;

        setParams(next);
        setFlash(previous => ({key, n: previous.n + 1}));
        updateParameters(next)
    }, [updateParameters, t]);

    // Refreshed on every render (no dependency array) so the measure bar always
    // writes through the current dispatch closure.
    React.useEffect(() => {
        apiRef.current = {
            apply: (key, value) => {
                if (editable) {
                    commit(key, value)
                }
            },
        };

        return () => {
            apiRef.current = null
        }
    });

    function handleCommit(key: ParameterKey, raw: string) {
        commit(key, parseFloat(raw))
    }

    function renderState() {
        if (parametersUpdating) {
            return <span className={styles.savestate}>{`● ${t("modern.saving")}`}</span>
        }

        if (localError) {
            return (
                <span className={styles.savestate__rejected} title={localError}>
                    {`✕ ${localError}`}
                </span>
            )
        }

        if (rejected) {
            return (
                <span
                    className={styles.savestate__rejected}
                    title={parametersError || undefined}
                >
                    {`✕ ${parametersError || t("modern.not_saved")}`}
                </span>
            )
        }

        return <span className={styles.pill}>{t("modern.auto_editable")}</span>
    }

    function renderHead() {
        return (
            <div className={styles.sect_head}>
                <span className={styles.microlabel}>{t("modern.measurements")}</span>
                {editable && renderState()}
            </div>
        )
    }

    if (params === null) {
        return (
            <section className={styles.sect}>
                {renderHead()}
                <span className={styles.empty}>{t("no_params")}</span>
            </section>
        )
    }

    return (
        <section className={styles.sect}>
            {renderHead()}

            <div className={styles.params}>
                {PARAM_DEFS.map(def => (
                    <ParamCell
                        // Re-keying the just-committed cell remounts only that
                        // node, which restarts the flash animation on repeat
                        // edits without the prototype's forced reflow hack.
                        key={def.key === flash.key ? `${def.key}-${flash.n}` : def.key}
                        def={def}
                        value={params[def.key]}
                        editable={editable && def.editable}
                        flashing={def.key === flash.key}
                        fullName={fullName(def)}
                        rangeText={def.rangeKey ? t(def.rangeKey) : def.range}
                        onCommit={handleCommit}
                        onEditingChange={handleEditingChange}
                    />
                ))}
            </div>
        </section>
    )
}

type ParamCellProps = {
    def: ParamDef
    value: number | undefined
    editable: boolean
    flashing: boolean
    fullName: string
    /** Already translated by the parent — this cell stays i18n-free. */
    rangeText: string
    onCommit(key: ParameterKey, raw: string): void
    onEditingChange(key: ParameterKey, editing: boolean): void
}

/**
 * @param props
 */
function ParamCell(props: ParamCellProps) {
    const styles = useStyles();
    const {def, value, editable, flashing, fullName, rangeText, onCommit, onEditingChange} = props;
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState("");
    // Escape must never write. Some browsers fire a blur when the focused input
    // is unmounted, which would otherwise let onBlur -> commit() save the very
    // value the user just cancelled — on a clinical measurement.
    const cancelledRef = React.useRef(false);

    const shown = typeof value === "number" ? String(value) : EM_DASH;

    // The parent re-keys a committed cell to restart its flash animation, which
    // unmounts this node while it may still be the open editor — report the
    // close from a cleanup so the page's Approve guard cannot latch on.
    React.useEffect(() => {
        onEditingChange(def.key, editing);

        return () => onEditingChange(def.key, false)
    }, [def.key, editing, onEditingChange]);

    function startEditing() {
        if (!editable) {
            return
        }

        cancelledRef.current = false;
        setDraft(typeof value === "number" ? String(value) : "");
        setEditing(true)
    }

    function cancel() {
        cancelledRef.current = true;
        setEditing(false)
    }

    function commit() {
        setEditing(false);

        if (cancelledRef.current) {
            cancelledRef.current = false;

            return
        }

        if (draft.trim() !== "") {
            onCommit(def.key, draft)
        }
    }

    return (
        <div
            className={`${styles.param} ${editable ? styles.param__editable : ""} ${flashing ? styles.param__flash : ""}`}
            onClick={startEditing}
            title={fullName}
        >
            <div className={styles.param_label}>
                {def.label}
                {def.heart && (
                    <svg className={styles.param_heart} viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 13.5C8 13.5 2 9.6 2 5.6A3.1 3.1 0 0 1 8 4a3.1 3.1 0 0 1 6 1.6c0 4-6 7.9-6 7.9z"/>
                    </svg>
                )}
            </div>
            <div className={styles.param_value}>
                {editing ? (
                    <input
                        autoFocus
                        type="text"
                        inputMode="decimal"
                        className={styles.param_input}
                        value={draft}
                        aria-label={fullName}
                        onChange={event => setDraft(event.target.value)}
                        onClick={event => event.stopPropagation()}
                        onBlur={commit}
                        onKeyDown={event => {
                            event.stopPropagation();

                            if (event.key === "Enter") {
                                commit()
                            }

                            if (event.key === "Escape") {
                                cancel()
                            }
                        }}
                    />
                ) : (
                    <>
                        {shown}
                        {shown !== EM_DASH ? <small>{def.unit}</small> : null}
                    </>
                )}
            </div>
            {rangeText ? <div className={styles.param_range}>{rangeText}</div> : null}
        </div>
    )
}

export {ModernParamsCard}

export default withRecordData(ModernParamsCard) as React.ComponentType<ModernParamsCardOwnProps>
